import { useRef, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import TiltWrapper from "../../TiltWrapper"

export default function FeaturesCarousel({ cards }) {
  const scrollRef = useRef(null)
  const cardRefs = useRef([])
  const dragRef = useRef({ dragging: false, startX: 0, scrollLeft: 0 })
  const autoScrollRef = useRef(null)
  const tripled = [...cards, ...cards, ...cards]
  const count = cards.length

  function updateScales() {
    const el = scrollRef.current
    if (!el) return
    const centerX = el.scrollLeft + el.offsetWidth / 2
    const halfWidth = el.offsetWidth / 2
    cardRefs.current.forEach((card) => {
      if (!card) return
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const distance = Math.abs(cardCenter - centerX)
      const scale = 1 - 0.5 * Math.min(1, distance / halfWidth)
      card.style.transform = `scale(${scale})`
    })
  }

  function startAutoScroll() {
    if (autoScrollRef.current) return
    let accum = 0
    function tick() {
      const el = scrollRef.current
      if (!el) return
      accum += 0.15
      if (accum >= 1) {
        el.scrollLeft += Math.floor(accum)
        accum -= Math.floor(accum)
        updateScales()
      }
      autoScrollRef.current = requestAnimationFrame(tick)
    }
    autoScrollRef.current = requestAnimationFrame(tick)
  }

  function stopAutoScroll() {
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null }
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let oneSet = 0
    function init() {
      oneSet = el.scrollWidth / 3
      el.scrollLeft = oneSet
      updateScales()
      startAutoScroll()
    }
    function onScroll() {
      if (!oneSet) return
      if (el.scrollLeft > oneSet * 1.9) el.scrollLeft -= oneSet
      else if (el.scrollLeft < oneSet * 0.1) el.scrollLeft += oneSet
      updateScales()
    }
    requestAnimationFrame(init)
    el.addEventListener("scroll", onScroll, { passive: true })
    el.addEventListener("wheel", stopAutoScroll, { passive: true })
    el.addEventListener("touchstart", stopAutoScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      el.removeEventListener("wheel", stopAutoScroll)
      el.removeEventListener("touchstart", stopAutoScroll)
      stopAutoScroll()
    }
  }, [count])

  function onMouseDown(e) {
    const el = scrollRef.current
    stopAutoScroll()
    if (dragRef.current.rafId) cancelAnimationFrame(dragRef.current.rafId)
    dragRef.current = { dragging: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, lastX: e.pageX, velocity: 0, rafId: null }
    el.style.cursor = "grabbing"
    el.style.userSelect = "none"
  }
  function onMouseMove(e) {
    if (!dragRef.current.dragging) return
    e.preventDefault()
    const el = scrollRef.current
    const x = e.pageX - el.offsetLeft
    dragRef.current.velocity = e.pageX - dragRef.current.lastX
    dragRef.current.lastX = e.pageX
    el.scrollLeft = dragRef.current.scrollLeft - (x - dragRef.current.startX)
  }
  function onMouseUp() {
    if (!dragRef.current.dragging) return
    dragRef.current.dragging = false
    const el = scrollRef.current
    if (el) { el.style.cursor = "grab"; el.style.userSelect = "" }
    let velocity = -dragRef.current.velocity
    function momentum() {
      if (!el || Math.abs(velocity) < 0.5) return
      el.scrollLeft += velocity
      velocity *= 0.94
      updateScales()
      dragRef.current.rafId = requestAnimationFrame(momentum)
    }
    dragRef.current.rafId = requestAnimationFrame(momentum)
  }

  return (
    <>
      <style>{`
        .feature-card-shadow { box-shadow: 0 12px 28px -5px var(--card-sc), 0 4px 12px -3px rgba(0,0,0,0.07); }
        @media (min-width: 768px) { .feature-card-shadow { box-shadow: 0 30px 60px -10px var(--card-sc), 0 10px 30px -5px rgba(0,0,0,0.10); } }
      `}</style>

      <div className="relative z-10 w-full">
        <div className="absolute left-0 top-0 bottom-0 w-20 lg:w-72 z-20 pointer-events-none"
          style={{ background: "linear-gradient(to right, rgb(var(--color-canvas)) 30%, transparent 100%)" }} />
        <div className="absolute right-0 top-0 bottom-0 w-20 lg:w-72 z-20 pointer-events-none"
          style={{ background: "linear-gradient(to left, rgb(var(--color-canvas)) 30%, transparent 100%)" }} />
        <div
          ref={scrollRef}
          style={{ overflowX: "auto", overflowY: "visible", scrollbarWidth: "none", msOverflowStyle: "none", cursor: "grab" }}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        >
          <div className="flex gap-3 md:gap-4 lg:gap-5 px-6 lg:px-12 pt-10 pb-6 md:pb-16 lg:pb-[100px]" style={{ width: "max-content" }}>
            {tripled.map((card, i) => {
              const Icon = card.icon
              return (
                <div key={i} ref={(el) => { cardRefs.current[i] = el }} className="flex-none w-[280px] md:w-[320px]" style={{ willChange: "transform" }}>
                  <TiltWrapper maxX={8} maxY={10}>
                    <div className="relative" style={{ transformStyle: "preserve-3d" }}>
                      <div className="feature-card-shadow absolute inset-0 rounded-2xl pointer-events-none"
                        style={{ "--card-sc": `${card.color}59`, transform: "translateZ(-1px)" }} />
                      <div
                        className="group relative min-h-[260px] md:min-h-[320px] flex flex-col gap-6 bg-paper border border-bordered/50 rounded-2xl p-8 overflow-hidden"
                        onMouseMove={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`)
                          e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`)
                        }}
                      >
                        <div className="absolute rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 ease-in-out pointer-events-none z-0"
                          style={{ backgroundColor: card.color, width: "800px", height: "800px", left: "-348px", top: "-348px" }} />

                        <div className="absolute inset-0 pointer-events-none z-[5] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          style={{ background: "radial-gradient(circle 180px at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.22) 0%, transparent 100%)" }} />

                        <div className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: card.color }}>
                          <Icon className="w-7 h-7 text-white mx-auto" />
                        </div>

                        <Typography 
                          variant="h4" 
                          className="relative z-10 text-text mb-2 mt-0 transition-colors duration-300 group-hover:text-white"
                        >
                          {card.title}
                        </Typography>

                        <Typography 
                          variant="body1_dim" 
                          className="block relative z-10 leading-relaxed transition-colors duration-300 group-hover:text-white/90 mt-auto"
                        >
                          {card.description}
                        </Typography>
                      </div>
                    </div>
                  </TiltWrapper>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}